import { createContext, use, useEffect, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { gql } from "@lytra/frontend-common/__generated__/gql";
import { useQuery } from "@apollo/client/react";
import { useProfile } from "@/providers/ProfileProvider";
import { TENANT_PARAM, useImpersonation } from "@/providers/ImpersonationProvider";

const ALL_TENANTS_QUERY = gql(`
  query AllTenants {
    adminAllTenants {
      id
      name
      slug
    }
  }
`);

interface Tenant {
  id: string;
  name: string;
  slug?: string | null;
}

interface TenantSelectionContextType {
  selectedTenantId: string | undefined;
  setSelectedTenantId: (tenantId: string | undefined) => void;
  tenants: Tenant[];
  loading: boolean;
}

const TenantSelectionContext = createContext<
  TenantSelectionContextType | undefined
>(undefined);

/** How a tenant is named in the URL: by slug, or by id when it has none. */
const tenantRef = (tenant: Tenant) => tenant.slug ?? tenant.id;

// Tenant ids by slug and by id, for every tenant listed so far. A tenant switch resets
// the Apollo store and remounts this provider, so the list is empty until it refetches;
// without these the slug in the URL names no tenant meanwhile and selectedTenantId
// blinks to undefined.
const knownTenantIds = new Map<string, string>();

export function TenantSelectionProvider({ children }: { children: ReactNode }) {
  const { userRoles, tenant } = useProfile();
  // Use the impersonation context which is above Apollo (manages the URL and localStorage)
  const { impersonateTenant, setImpersonateTenant } = useImpersonation();
  const [searchParams] = useSearchParams();
  const urlTenant = searchParams.get(TENANT_PARAM);

  // Fetch all tenants (only for lytra_staff)
  const { data: tenantsData, loading: tenantsLoading } = useQuery(
    ALL_TENANTS_QUERY,
    {
      skip: !userRoles.isLytraStaff,
    },
  );

  const tenants = tenantsData?.adminAllTenants ?? [];
  const impersonatedTenant = tenants.find(
    (t) => t.slug === impersonateTenant || t.id === impersonateTenant,
  );

  useEffect(() => {
    for (const t of tenantsData?.adminAllTenants ?? []) {
      knownTenantIds.set(tenantRef(t), t.id).set(t.id, t.id);
    }
  }, [tenantsData]);

  // A non-staff user ignores a tenant in the URL (ImpersonationProvider); drop it so the
  // links they follow from here do not carry it on.
  useEffect(() => {
    if (!userRoles.isLytraStaff && urlTenant !== null) {
      setImpersonateTenant(undefined);
    }
  }, [userRoles.isLytraStaff, urlTenant, setImpersonateTenant]);

  // Put the staff user's tenant in the URL: the remembered one, or the first tenant when
  // none (or an unknown one) is named. A tenant that has a slug is named by it.
  useEffect(() => {
    if (!userRoles.isLytraStaff || tenants.length === 0) return;
    const ref = tenantRef(impersonatedTenant ?? tenants[0]);
    if (urlTenant !== ref) {
      setImpersonateTenant(ref);
    }
  }, [
    tenants,
    impersonatedTenant,
    urlTenant,
    userRoles.isLytraStaff,
    setImpersonateTenant,
  ]);

  // Non-staff users don't impersonate — they only ever act on their own tenant.
  const selectedTenantId = userRoles.isLytraStaff
    ? (impersonatedTenant?.id ?? knownTenantIds.get(impersonateTenant ?? ""))
    : tenant.id;

  const setSelectedTenantId = (tenantId: string | undefined) => {
    const selected = tenants.find((t) => t.id === tenantId);
    setImpersonateTenant(selected ? tenantRef(selected) : tenantId);
  };

  return (
    <TenantSelectionContext
      value={{
        selectedTenantId,
        setSelectedTenantId,
        tenants,
        loading: tenantsLoading,
      }}
    >
      {children}
    </TenantSelectionContext>
  );
}

export function useTenantSelection() {
  const context = use(TenantSelectionContext);
  if (context === undefined) {
    throw new Error(
      "useTenantSelection must be used within a TenantSelectionProvider",
    );
  }
  return context;
}
